import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePresence } from '../../context/PresenceContext';
import './globalPresence.css';
import usePromotionAnimations from './usePromotionAnimations';
import usePresenceSound from './usePresenceSound';

// GlobalPresence is a system-level visualization inspired by OrbitingFriends'
// orbiting heuristics, but generalized: each live session is an anonymous orb.
export default function GlobalPresence({
  selfId,
  liveCount = 0,
  playerStats,
  seedCount = 3,
  energy = 0.2,
  soundEnabled = false,
}) {
  const presenceMap = usePresence()?.presenceMap || {};
  const { promotionSet, corePulse } = usePromotionAnimations(presenceMap);
  const [surgeActive, setSurgeActive] = useState(false);
  const prevIdsRef = useRef(new Set());
  const surgeTimesRef = useRef([]);
  const prevLiveRef = useRef(liveCount);

  usePresenceSound({
    presenceMap,
    enabled: soundEnabled,
    liveCount,
    playerStats,
  });

  // Track join bursts locally to sync a subtle visual surge cue with the sound logic.
  const triggerSurge = () => {
    setSurgeActive(true);
    setTimeout(() => setSurgeActive(false), 320);
  };

  useEffect(() => {
    const currentIds = Object.keys(presenceMap || {});
    const prev = prevIdsRef.current;
    let newJoins = 0;
    currentIds.forEach((id) => {
      if (!prev.has(id)) newJoins += 1;
    });
    const now = Date.now();
    if (newJoins > 0) {
      const arr = surgeTimesRef.current.filter((t) => now - t <= 2000);
      for (let i = 0; i < newJoins; i += 1) arr.push(now);
      surgeTimesRef.current = arr;
      if (arr.length >= 3) triggerSurge();
    }
    prevIdsRef.current = new Set(currentIds);
  }, [presenceMap]);

  // Also respect metric-level jumps (e.g., liveCount burst).
  useEffect(() => {
    const prev = prevLiveRef.current || 0;
    const delta = liveCount - prev;
    if (delta >= 3) triggerSurge();
    prevLiveRef.current = liveCount;
  }, [liveCount]);

  const nodes = useMemo(() => {
    const entries = Object.entries(presenceMap || {});
    const mappedEntries = entries
      .map(([id, p]) => ({
        id,
        status: p?.status || (p?.guest ? 'guest' : 'offline'),
        isGuest: !!p?.guest,
      }))
      .filter((n) => n.status);

    // Partition by role/state for ring ordering.
    const playerEntries = mappedEntries.filter((n) => !n.isGuest);
    const guestEntries = mappedEntries.filter((n) => n.isGuest && n.status !== 'offline');

    // Always reflect the live visitor count exactly by seeding guests to match.
    const targetGuests = Math.max(liveCount || 0, seedCount || 0);
    const currentGuests = guestEntries.length;
    if (targetGuests > currentGuests) {
      const missing = targetGuests - currentGuests;
      for (let i = 0; i < missing; i += 1) {
        guestEntries.push({ id: `guest-seed-${i}`, status: 'guest', isGuest: true });
      }
    } else if (targetGuests > 0 && currentGuests > targetGuests) {
      guestEntries.length = targetGuests;
    }

    // Fallback for players when no mapped entries exist: seed from playerStats.
    if (!playerEntries.length && playerStats) {
      const online = playerStats.online || 0;
      const idle = playerStats.idle || 0;
      const offline = playerStats.offline || 0;

      for (let i = 0; i < online; i += 1) {
        playerEntries.push({ id: `seed-online-${i}`, status: 'online', isGuest: false });
      }
      for (let i = 0; i < idle; i += 1) {
        playerEntries.push({ id: `seed-idle-${i}`, status: 'idle', isGuest: false });
      }
      for (let i = 0; i < offline; i += 1) {
        playerEntries.push({ id: `seed-offline-${i}`, status: 'offline', isGuest: false });
      }
    }

    const distributeToRings = (list, opts) => {
      if (!list.length) return [];
      const {
        baseRadius,
        ringGap,
        maxPerRing,
        radiusJitter = 0,
        driftBase,
        driftVariance,
        durationForStatus,
        ring,
      } = opts;
      const nodesOut = [];
      let ringIndex = 0;
      let cursor = 0;

      while (cursor < list.length) {
        const slice = list.slice(cursor, cursor + maxPerRing);
        const step = 360 / slice.length;
        // Gentle per-ring rotation to avoid a perfectly mechanical wheel.
        const angleOffset = ((ringIndex * 11) % 360) + (ringIndex % 2 ? step * 0.2 : step * 0.05);
        const ringRadius = baseRadius + ringGap * ringIndex;

        slice.forEach((entry, idx) => {
          const status = (entry.status || 'offline').toLowerCase();
          const angle = angleOffset + step * idx;
          const jitter =
            radiusJitter && slice.length > 1
              ? ((idx % 4) - 1.5) * radiusJitter * 0.6
              : 0;
          const isSelf = selfId && String(selfId) === String(entry.id);
          const promoting = promotionSet.has(entry.id);

          let radius = ringRadius + jitter;
          let drift = driftBase + (idx % 3) * driftVariance;
          let duration = durationForStatus(status);

          // Promotion easing: tighten player rings; keep guests outer.
          if (promoting && ring === 'inner') {
            radius *= 0.88;
            drift *= 0.82;
            duration *= 0.9;
          }

          nodesOut.push({
            ...entry,
            angle,
            radius,
            duration,
            drift,
            isSelf,
            ring,
            promoting,
          });
        });

        cursor += maxPerRing;
        ringIndex += 1;
      }

      return nodesOut;
    };

    const onlineEntries = playerEntries.filter(
      (n) => (n.status || '').toLowerCase() === 'online'
    );
    const idleEntries = playerEntries.filter((n) => {
      const s = (n.status || '').toLowerCase();
      return s === 'idle' || s === 'away' || s === 'background';
    });
    const offlineEntries = playerEntries.filter((n) => {
      const s = (n.status || '').toLowerCase();
      return s !== 'online' && s !== 'idle' && s !== 'away' && s !== 'background';
    });

    // Compact bases keep rings closer to the core and avoid runaway radius at high counts.
    const ringGap = 12;
    const baseOnline = 54;
    const baseIdle = 70;
    const baseOffline = 88;
    const baseGuest = 108;
    const guestRingGap = 16;

    const playerDurations = (status) => {
      if (status === 'online') return 7500;
      if (status === 'idle' || status === 'away' || status === 'background') return 11500;
      return 16000; // offline/recent
    };

    const nodesOnline = distributeToRings(onlineEntries, {
      baseRadius: baseOnline,
      ringGap: guestRingGap,
      maxPerRing: 12,
      radiusJitter: 4,
      driftBase: 7,
      driftVariance: 3,
      durationForStatus: playerDurations,
      ring: 'inner',
    });

    const nodesIdle = distributeToRings(idleEntries, {
      baseRadius: baseIdle,
      ringGap,
      maxPerRing: 12,
      radiusJitter: 4,
      driftBase: 7,
      driftVariance: 3,
      durationForStatus: playerDurations,
      ring: 'inner',
    });

    const nodesOffline = distributeToRings(offlineEntries, {
      baseRadius: baseOffline,
      ringGap,
      maxPerRing: 12,
      radiusJitter: 4,
      driftBase: 6,
      driftVariance: 3,
      durationForStatus: playerDurations,
      ring: 'inner',
    });

    const guestNodes = distributeToRings(guestEntries, {
      baseRadius: baseGuest,
      ringGap,
      maxPerRing: 14,
      radiusJitter: 3,
      driftBase: 6,
      driftVariance: 3,
      durationForStatus: () => 14000,
      ring: 'outer',
    });

    return [...nodesOnline, ...nodesIdle, ...nodesOffline, ...guestNodes];
  }, [presenceMap, selfId, seedCount, playerStats, liveCount, promotionSet]);

  if (!nodes.length) return null;

  return (
    <div
      className="gp-shell"
      aria-label="Global Presence"
      style={{ '--gp-energy': energy, '--gp-surge': surgeActive ? 1 : 0 }}
    >
      <div
        className={`gp-core${corePulse ? ' gp-core-pulsing' : ''}${surgeActive ? ' gp-core-surge' : ''}`}
        aria-label="Presence core"
      >
        <div className="gp-core-inner" />
        {selfId ? (
      <div className="gp-core-self" aria-label="You" />
    ) : (
      <div className="gp-core-pulse" />
    )}
  </div>
  <div className={`gp-orbit${surgeActive ? ' gp-orbit-surge' : ''}`}>
        {nodes.map((n) => (
          <div
            key={n.id}
            className={`gp-node gp-status-${n.status}${n.isSelf ? ' gp-self' : ''}${
              n.ring === 'outer' ? ' gp-ring-outer' : ''
            }${n.promoting ? ' gp-promoting' : ''}`}
            style={{
              '--gp-angle': `${n.angle}deg`,
              '--gp-radius': `${n.radius}px`,
              '--gp-drift': `${n.drift}px`,
              '--gp-duration': `${n.promoting ? n.duration * 0.85 : n.duration}ms`,
            }}
            aria-label={n.isSelf ? 'You (live session)' : 'Live session'}
          />
        ))}
      </div>
    </div>
  );
}
